import { Link } from "react-router-dom";
import { ArrowRight, Target, Users, Award, Globe } from "lucide-react";
import TrustBar from "@/components/TrustBar";
import Seo from "@/components/Seo";

const VALUES = [
    { icon: Target, title: "Outcome-first", desc: "We&apos;re only paid when you are. So every recommendation is built around the strongest possible outcome — not commissions or quotas." },
    { icon: Users, title: "Specialist matched", desc: "Every case is matched with an SRA-regulated specialist firm in the exact practice area you need. Never a generalist." },
    { icon: Award, title: "FCA-aware", desc: "We follow FCA standards for claims-management activity, work within FCA fee caps and disclose every fee in writing before any claim begins. No surprises, no hidden charges." },
    { icon: Globe, title: "UK-wide reach", desc: "From Penzance to Inverness — we serve England, Wales, Scotland and Northern Ireland." },
];

export default function AboutPage() {
    return (
        <>
            <Seo
                title="About ClaimHub UK"
                description="Britain's most-trusted claims hub — rebuilt for 2026. Specialist solicitors, FCA-aware ops, £218m+ in client compensation across 10 sectors."
                path="/about"
            />
            <section className="relative overflow-hidden bg-navy-900 text-white">
                <div className="absolute inset-0 bg-mesh-emerald" />
                <div className="absolute inset-0 grid-bg-dark opacity-25" />
                <div className="container-x relative py-20 lg:py-28">
                    <span className="text-eyebrow-light">About us</span>
                    <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight max-w-3xl leading-[1.05]">
                        Britain&apos;s most-trusted claims hub — rebuilt for 2026.
                    </h1>
                    <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
                        We&apos;re a specialist claims firm based in Covent Garden, London. We exist because UK consumers deserve better than yesterday&apos;s claims-management companies. We replaced cold calls with a calm, digital-first process — and replaced confusion with full FCA-aware transparency.
                    </p>
                </div>
            </section>

            <TrustBar />

            <section className="section-pad bg-white">
                <div className="container-x grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <span className="text-eyebrow">Our mission</span>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-display font-bold tracking-tight text-navy-900">
                            To make claiming what&apos;s yours feel as easy as ordering a coffee.
                        </h2>
                        <p className="mt-5 text-base text-slate-600 leading-relaxed">
                            Millions of pounds in UK compensation goes unclaimed every year — not because people don&apos;t qualify, but because the old process was confusing, slow and built around the firm, not the client. We rewired the whole thing.
                        </p>
                        <p className="mt-4 text-base text-slate-600 leading-relaxed">
                            ClaimHub UK was founded by ex-FCA, fintech and senior litigation professionals who&apos;d had enough of seeing good claims die in spreadsheets. Today we cover 10 specialist sectors, work with a curated panel of SRA-regulated solicitor partners, and have facilitated over £218m of compensation for UK consumers.
                        </p>
                    </div>

                    <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
                        {VALUES.map((v) => (
                            <div key={v.title} className="card-soft card-soft-hover p-7">
                                <div className="w-10 h-10 rounded-xl bg-navy-900 flex items-center justify-center">
                                    <v.icon className="w-5 h-5 text-emerald-400" />
                                </div>
                                <h3 className="mt-5 text-lg font-display font-semibold text-navy-900">{v.title}</h3>
                                <p className="mt-2 text-sm text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: v.desc }} />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section-pad bg-slate-50">
                <div className="container-x">
                    <div className="grid lg:grid-cols-3 gap-5">
                        {[
                            { label: "Founded", value: "2018", note: "Covent Garden, London" },
                            { label: "Sectors covered", value: "10", note: "From PCP to pensions" },
                            { label: "Client compensation", value: "£218m+", note: "Recovered to date" },
                        ].map((s) => (
                            <div key={s.label} className="card-soft p-8">
                                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">{s.label}</div>
                                <div className="mt-3 text-5xl font-display font-bold text-navy-900 tracking-tight">{s.value}</div>
                                <div className="mt-2 text-sm text-slate-500">{s.note}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-white">
                <div className="container-x py-20">
                    <div className="rounded-3xl bg-navy-900 text-white p-10 lg:p-14 relative overflow-hidden">
                        <div className="absolute inset-0 bg-mesh-emerald opacity-90" />
                        <div className="relative grid lg:grid-cols-2 gap-8 items-center">
                            <div>
                                <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Got a claim in mind?</h2>
                                <p className="mt-3 text-slate-300 max-w-md">It takes 60 seconds. Find out if you qualify with zero risk and zero obligation.</p>
                            </div>
                            <div className="lg:text-right">
                                <Link to="/claim" className="btn-primary">Start your free check <ArrowRight className="w-4 h-4" /></Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
